import React, { ReactElement, useMemo } from 'react';
import { Currency } from 'hydra-data-types';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import { formatWholePrice } from '../../../util/Currency';
import { ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { ChoiceItem, SingleSelectLayout } from '../layouts/SingleSelectLayout';

export type PapHouseholdIncomeLayoutConfig = Record<string, never>;

export type PapHouseholdIncomeScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.PapHouseholdIncome,
  PapHouseholdIncomeLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PapHouseholdIncomeScreenConfig<ScreenNames>,
  ScreenNames
>;

/** Income thresholds per currency in 100 subunits eg. cents . */
const INCOME_THRESHOLDS = {
  [Currency.USD]: { low: 4088000, high: 8176000 },
  [Currency.AUD]: { low: 7389300, high: 14778700 },
  [Currency.GBP]: { low: 3000000, high: 6000000 },
  [Currency.CAD]: { low: 4000000, high: 8000000 },
  [Currency.NZD]: { low: 5000000, high: 10000000 },
  [Currency.SEK]: { low: 25000000, high: 50000000 },
  [Currency.NOK]: { low: 35000000, high: 70000000 },
  [Currency.EUR]: { low: 2500000, high: 5000000 },
} as const;

function buildChoices(currency: Currency): ChoiceItem[] {
  const { low, high } = INCOME_THRESHOLDS[currency];
  const lowFormatted = formatWholePrice(low, currency);
  const highFormatted = formatWholePrice(high, currency);
  // low threshold + 1 cent for the mid-range lower bound
  const lowPlusOne = formatWholePrice(low + 100, currency);
  return [
    { value: 'low', label: `Less than ${lowFormatted}` },
    { value: 'mid', label: `${lowPlusOne} - ${highFormatted}` },
    { value: 'high', label: `More than ${highFormatted}` },
  ];
}

const BODY =
  'Please select the range that best represents your total estimated annual household income (before tax)';

export const PapHouseholdIncomeScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const currency = useAppStore((state) => state.pricing.currency);
  const choices = useMemo(() => buildChoices(currency), [currency]);

  function onSelected(value: string) {
    dispatchUserData({ [props.screenName]: [value] });
    props.navigation.next(props);
  }

  return (
    <ScreenWrapper>
      <SingleSelectLayout body={[BODY]} choices={choices} onSelected={onSelected} />
    </ScreenWrapper>
  );
};
